import { LitElement, css, html, nothing } from "lit";
import type { PropertyValues, TemplateResult } from "lit";
import type {
  ConditionField,
  ConditionMatch,
  ConditionOperator,
  Feed,
  Rule,
  RuleActionKind,
  RuleCondition,
  RuleInput,
} from "@newsfeed/shared";
import { sharedStyles } from "../styles.js";

const FIELDS: ReadonlyArray<ConditionField> = ["title", "content", "author"];
const OPERATORS: ReadonlyArray<ConditionOperator> = ["contains", "matches"];
const ACTION_LABEL: Record<RuleActionKind, string> = {
  mark_read: "Mark read",
  keep: "Keep",
};

/**
 * Create/edit form for an automation rule. Builds a RuleInput from its fields
 * and dispatches `rule-save` (detail `{ input }`); `rule-cancel` to dismiss.
 * `buildInput()` is exposed as the testable seam.
 */
export class NfRuleForm extends LitElement {
  static override properties = {
    feeds: { attribute: false },
    rule: { attribute: false },
    name: { state: true },
    conditionMatch: { state: true },
    conditions: { state: true },
    actions: { state: true },
    watchedFeedUrl: { state: true },
  };

  feeds: Feed[] = [];
  rule: Rule | null = null;
  name = "";
  conditionMatch: ConditionMatch = "match_all";
  conditions: RuleCondition[] = [];
  actions: RuleActionKind[] = ["mark_read"];
  watchedFeedUrl: string | null = null;

  static override styles = [
    sharedStyles,
    css`
      :host {
        display: block;
      }
      .field {
        display: flex;
        flex-direction: column;
        gap: var(--nf-space-4);
        margin-bottom: var(--nf-space-12);
      }
      label {
        font-weight: 600;
        font-size: var(--nf-font-14);
      }
      .cond {
        display: flex;
        gap: var(--nf-space-6);
        margin-bottom: var(--nf-space-6);
        align-items: center;
      }
      .cond input {
        flex: 1;
      }
      .actions-row {
        display: flex;
        gap: var(--nf-space-16);
      }
      .checkbox {
        display: flex;
        gap: var(--nf-space-6);
        align-items: center;
        font-weight: 400;
      }
      .footer {
        display: flex;
        gap: var(--nf-space-8);
        margin-top: var(--nf-space-16);
      }
    `,
  ];

  /** Sync editable state from a provided rule when editing begins. */
  override willUpdate(changed: PropertyValues<this>): void {
    if (changed.has("rule") && this.rule) {
      this.name = this.rule.name;
      this.conditionMatch = this.rule.conditionMatch;
      this.conditions = this.rule.conditions.map((c) => ({ ...c }));
      this.actions = this.rule.actions.map((a) => a.action);
      this.watchedFeedUrl = this.rule.watchedFeedUrl;
    }
  }

  /** Assemble the current form state into a RuleInput. */
  buildInput(): RuleInput {
    return {
      name: this.name.trim(),
      trigger: "new_article",
      watchedFeedUrl: this.watchedFeedUrl,
      conditionMatch: this.conditionMatch,
      conditions: this.conditions.map((c) => ({ ...c })),
      actions: this.actions.map((action) => ({ action })),
    };
  }

  private get valid(): boolean {
    return this.name.trim() !== "" && this.actions.length > 0;
  }

  private addCondition(): void {
    this.conditions = [
      ...this.conditions,
      { field: "title", operator: "contains", query: "" },
    ];
  }

  private updateCondition(index: number, patch: Partial<RuleCondition>): void {
    this.conditions = this.conditions.map((c, i) =>
      i === index ? { ...c, ...patch } : c,
    );
  }

  private removeCondition(index: number): void {
    this.conditions = this.conditions.filter((_, i) => i !== index);
  }

  private toggleAction(kind: RuleActionKind, on: boolean): void {
    this.actions = on
      ? [...new Set([...this.actions, kind])]
      : this.actions.filter((a) => a !== kind);
  }

  private save(): void {
    if (!this.valid) return;
    this.dispatchEvent(
      new CustomEvent("rule-save", {
        detail: { input: this.buildInput() },
        bubbles: true,
        composed: true,
      }),
    );
  }

  override render(): TemplateResult {
    return html`
      <div class="field">
        <label for="rf-name">Name</label>
        <input
          id="rf-name"
          .value=${this.name}
          @input=${(e: Event) => {
            if (e.target instanceof HTMLInputElement)
              this.name = e.target.value;
          }}
        />
      </div>

      <div class="field">
        <label for="rf-scope">Scope</label>
        <select
          id="rf-scope"
          @change=${(e: Event) => {
            if (e.target instanceof HTMLSelectElement)
              this.watchedFeedUrl =
                e.target.value === "" ? null : e.target.value;
          }}
        >
          <option value="" ?selected=${this.watchedFeedUrl === null}>
            All feeds
          </option>
          ${this.feeds.map(
            (f) =>
              html`<option
                value=${f.url}
                ?selected=${this.watchedFeedUrl === f.url}
              >
                ${f.title ?? f.url}
              </option>`,
          )}
        </select>
      </div>

      <div class="field">
        <label for="rf-match">Match</label>
        <select
          id="rf-match"
          @change=${(e: Event) => {
            if (e.target instanceof HTMLSelectElement)
              this.conditionMatch =
                e.target.value === "match_any" ? "match_any" : "match_all";
          }}
        >
          <option
            value="match_all"
            ?selected=${this.conditionMatch === "match_all"}
          >
            All conditions
          </option>
          <option
            value="match_any"
            ?selected=${this.conditionMatch === "match_any"}
          >
            Any condition
          </option>
        </select>
      </div>

      <div class="field">
        <label
          >Conditions
          ${this.conditions.length === 0 ? html`<span class="muted">(empty = every article)</span>` : nothing}</label
        >
        ${this.conditions.map(
          (cond, i) =>
            html`<div class="cond">
              <select
                aria-label="Condition field"
                @change=${(e: Event) => {
                  if (e.target instanceof HTMLSelectElement)
                    this.updateCondition(i, {
                      field: e.target.value as ConditionField,
                    });
                }}
              >
                ${FIELDS.map((f) => html`<option value=${f} ?selected=${cond.field === f}>${f}</option>`)}
              </select>
              <select
                aria-label="Condition operator"
                @change=${(e: Event) => {
                  if (e.target instanceof HTMLSelectElement)
                    this.updateCondition(i, {
                      operator: e.target.value as ConditionOperator,
                    });
                }}
              >
                ${OPERATORS.map((o) => html`<option value=${o} ?selected=${cond.operator === o}>${o}</option>`)}
              </select>
              <input
                aria-label="Condition query"
                placeholder="query"
                .value=${cond.query}
                @input=${(e: Event) => {
                  if (e.target instanceof HTMLInputElement)
                    this.updateCondition(i, { query: e.target.value });
                }}
              />
              <button
                aria-label="Remove condition"
                @click=${() => this.removeCondition(i)}
              >
                ✕
              </button>
            </div>`,
        )}
        <button @click=${() => this.addCondition()}>+ Add condition</button>
      </div>

      <div class="field">
        <label>Actions</label>
        <div class="actions-row">
          ${(Object.keys(ACTION_LABEL) as RuleActionKind[]).map(
            (kind) =>
              html`<label class="checkbox">
                <input
                  type="checkbox"
                  .checked=${this.actions.includes(kind)}
                  @change=${(e: Event) => {
                    if (e.target instanceof HTMLInputElement)
                      this.toggleAction(kind, e.target.checked);
                  }}
                />
                ${ACTION_LABEL[kind]}
              </label>`,
          )}
        </div>
      </div>

      <div class="footer">
        <button
          class="primary"
          ?disabled=${!this.valid}
          @click=${() => this.save()}
        >
          ${this.rule ? "Save changes" : "Create rule"}
        </button>
        <button
          @click=${() =>
            this.dispatchEvent(
              new CustomEvent("rule-cancel", { bubbles: true, composed: true }),
            )}
        >
          Cancel
        </button>
      </div>
    `;
  }
}

customElements.define("nf-rule-form", NfRuleForm);

declare global {
  interface HTMLElementTagNameMap {
    "nf-rule-form": NfRuleForm;
  }
}
